import { mat4 } from "gl-matrix";

export function applyAwakeCameraStates(runtime) {
    runtime?.forEach(entry => {
        if (!entry.authoringDeferred && entry.type === "Camera" && entry.applyOnAwake === true) {
            entry.applyOption(entry.currentOption, { forceImmediate: true, suppressUiUpdate: true });
        }
    });
    // Group presets may select a camera and must win over its atomic default.
    runtime?.forEach(entry => {
        if (!entry.authoringDeferred && entry.type === "Group" && entry.applyOnAwake === true) {
            entry.applyOption(entry.currentOption, { forceImmediate: true, suppressUiUpdate: true });
        }
    });
}

// Cache only within one lookup: model placement and reference nodes can change.
export function getNodeWorldMatrix(state, gltf, nodeIndex, parentLookup, cache) {
    if (!Number.isInteger(nodeIndex) || nodeIndex < 0) {
        return null;
    }

    const matrices = cache instanceof Map ? cache : new Map();
    if (matrices.has(nodeIndex)) {
        return matrices.get(nodeIndex);
    }

    const object = state?.nodeIndexToObject?.get(nodeIndex) ?? null;
    if (object) {
        object.updateWorldMatrix(true, false);
        const elements = object.matrixWorld.elements;
        const worldMatrix = mat4.fromValues(
            elements[0], elements[1], elements[2], elements[3],
            elements[4], elements[5], elements[6], elements[7],
            elements[8], elements[9], elements[10], elements[11],
            elements[12], elements[13], elements[14], elements[15]
        );
        matrices.set(nodeIndex, worldMatrix);
        return worldMatrix;
    }

    const node = gltf?.nodes?.[nodeIndex];
    if (!node) {
        return null;
    }

    let localMatrix = mat4.create();
    if (Array.isArray(node.matrix) && node.matrix.length === 16) {
        localMatrix = mat4.fromValues(
            node.matrix[0], node.matrix[1], node.matrix[2], node.matrix[3],
            node.matrix[4], node.matrix[5], node.matrix[6], node.matrix[7],
            node.matrix[8], node.matrix[9], node.matrix[10], node.matrix[11],
            node.matrix[12], node.matrix[13], node.matrix[14], node.matrix[15]
        );
    } else {
        const translation = Array.isArray(node.translation) ? node.translation : [0, 0, 0];
        const rotation = Array.isArray(node.rotation) ? node.rotation : [0, 0, 0, 1];
        const scale = Array.isArray(node.scale) ? node.scale : [1, 1, 1];
        mat4.fromRotationTranslationScale(localMatrix, rotation, translation, scale);
    }

    const parentIndex = parentLookup instanceof Map ? parentLookup.get(nodeIndex) : undefined;
    if (parentIndex !== undefined) {
        const parentMatrix = getNodeWorldMatrix(state, gltf, parentIndex, parentLookup, matrices);
        const worldMatrix = mat4.create();
        if (parentMatrix) {
            mat4.multiply(worldMatrix, parentMatrix, localMatrix);
        } else {
            mat4.copy(worldMatrix, localMatrix);
        }
        matrices.set(nodeIndex, worldMatrix);
        return worldMatrix;
    }

    matrices.set(nodeIndex, localMatrix);
    return localMatrix;
}
